import React, { useState } from 'react';
import { Col, Label, Button } from 'reactstrap';
import Swal from 'sweetalert2';
import { formatDateToddmmyyhhmm } from '../../../utils/dateformatter';
import {
  getOwnClauseResponse,
  hasClauseResponse,
  isEvaluatorResponse,
  needsIcReplyOnEvaluatorThread,
  getLastOwnEditableResponse,
  canAgreeLastCounter,
  toClauseResponseList,
  sortRespondClauseDetails,
  toLockedFlag,
} from '../respond';
import AgreeLockButton from './AgreeLockButton';
import { RespondNotesEditor, RespondMessageBubble } from '../ui';

const MandatoryClauseBadge = () => (
  <span className='badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill fw-medium fs-11'>
    Mandatory
  </span>
);

const ClauseHtmlContent = ({ value }) => {
  if (value == null || typeof value === 'object') {
    return <div className='text-dark mb-0 lh-base fs-13'>-</div>;
  }
  const text = String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  if (!text) {
    return <div className='text-dark mb-0 lh-base fs-13'>-</div>;
  }
  return (
    <div
      className='text-dark mb-0 lh-base fs-13'
      dangerouslySetInnerHTML={{ __html: String(value) }}
    />
  );
};

const hasClauseDescription = value => {
  if (value == null || typeof value === 'object') return false;
  const text = String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return text.length > 0;
};

const formatResponseMetaDate = response => {
  const value = response?.updatedDate || response?.createdDate;
  if (!value) return null;
  return formatDateToddmmyyhhmm(value) || null;
};

const ClauseCard = ({
  clause,
  index,
  clauseResponses,
  draftNotes,
  expandedClauses,
  formMain: _formMain,
  isClauseResponsesSaving,
  isClauseResponsesLoading,
  isEvaluatorView = true,
  handleDraftChange,
  handleClauseSecondaryAction,
  handleClauseSave,
  toggleClauseExpand,
  setErrors,
  onLockResponse,
  canInitiate = false,
  isRespondClosed = false,
  highlightPending = false,
  anchorId,
}) => {
  const responseList = sortRespondClauseDetails(
    toClauseResponseList(clauseResponses[clause.id])
  );
  const ownResponse = getOwnClauseResponse(responseList, {
    isEvaluator: isEvaluatorView,
  });
  const messageCount = responseList.filter(item =>
    hasClauseResponse(item?.respondNotes)
  ).length;
  const hasAnySaved = messageCount > 0;
  const hasCounterResponse = responseList.some(
    item => hasClauseResponse(item?.respondNotes) && !isEvaluatorResponse(item)
  );
  const lastNeedsIcReply = needsIcReplyOnEvaluatorThread(responseList);
  const lastOwnEditable = getLastOwnEditableResponse(responseList, {
    isEvaluator: isEvaluatorView,
  });
  const lastMessage = responseList.filter(item =>
    hasClauseResponse(item?.respondNotes)
  );
  const isThreadLocked = toLockedFlag(
    lastMessage[lastMessage.length - 1]?.isLocked
  );

  const isExpanded =
    expandedClauses[clause.id] !== undefined
      ? Boolean(expandedClauses[clause.id])
      : hasAnySaved;

  const savedNotes = lastOwnEditable?.respondNotes ?? '';
  const hasOwnSaved = hasClauseResponse(savedNotes);
  const draftValue = draftNotes[clause.id] ?? (hasOwnSaved ? savedNotes : '');
  const isDraftDirty = String(draftValue ?? '') !== String(savedNotes ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const canAddRespond = isRespondClosed
    ? false
    : isEvaluatorView
      ? (hasCounterResponse || canInitiate) && !hasOwnSaved
      : lastNeedsIcReply;
  const showRespondButton = canAddRespond && !isEditing && !isThreadLocked;
  const showRespondEditor = !isRespondClosed && !isThreadLocked && isEditing;
  const isMandatory = clause.isShowToDMUT === true;

  return (
    <Col sm={12} className='mb-2' id={anchorId}>
      <div
        className={`rounded-3 overflow-hidden bg-white ${
          highlightPending
            ? 'border border-danger border-2 shadow-sm'
            : isExpanded
              ? 'border border-primary shadow-sm'
              : 'border'
        }`}
        style={highlightPending ? { scrollMarginTop: 96 } : undefined}
      >
        <div className='px-3 py-3 d-flex align-items-center gap-3 border-bottom bg-light-subtle'>
          {index != null && (
            <span
              className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-semibold fs-12 ${
                isExpanded
                  ? 'bg-primary text-white'
                  : 'bg-light text-muted border'
              }`}
              style={{ width: '28px', height: '28px' }}
            >
              {index}
            </span>
          )}
          <div className='flex-grow-1 min-w-0'>
            <div className='d-flex align-items-center gap-2 flex-wrap'>
              <span
                className={`fs-14 mb-0 ${
                  isExpanded ? 'fw-bold text-dark' : 'fw-semibold text-dark'
                }`}
              >
                {clause.clauseTitle}
              </span>
              {isMandatory && <MandatoryClauseBadge />}
            </div>
          </div>
        </div>

        <div className='p-3'>
          {isMandatory && (
            <div className='text-danger small fw-medium mb-2'>
              * Mandatory clause for DMUT to fulfill
            </div>
          )}
          {hasClauseDescription(clause.clauseDescription) && (
            <div className='p-3 rounded-3 bg-light-subtle border text-muted fs-13 lh-base'>
              <ClauseHtmlContent value={clause.clauseDescription} />
            </div>
          )}

          <div
            className={`border-top pt-3 d-flex justify-content-between align-items-center${
              hasClauseDescription(clause.clauseDescription) ? ' mt-3' : ''
            }`}
          >
            <div
              className='d-flex align-items-center gap-2'
              style={{ cursor: 'pointer' }}
              onClick={() => toggleClauseExpand(clause.id)}
            >
              <span
                className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                  isExpanded
                    ? 'bg-primary-subtle text-primary'
                    : 'bg-light text-muted'
                }`}
                style={{ width: '28px', height: '28px' }}
              >
                <i
                  className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line fs-16`}
                ></i>
              </span>
              {hasAnySaved ? (
                <>
                  <span
                    className='bg-danger text-white rounded-pill d-inline-flex align-items-center justify-content-center fw-semibold fs-11'
                    style={{
                      minWidth: '20px',
                      height: '20px',
                      padding: messageCount > 9 ? '0 6px' : 0,
                    }}
                  >
                    {messageCount}
                  </span>
                  <span className='fw-semibold text-dark fs-13'>Response</span>
                </>
              ) : (
                <span className='text-muted fs-13'>No responses yet</span>
              )}
            </div>

            {showRespondButton && (
              <Button
                color='light'
                size='sm'
                className='border bg-white text-muted px-3 py-1 d-flex align-items-center gap-1 fs-13 rounded-2 shadow-sm'
                onClick={() => {
                  if (!isExpanded) toggleClauseExpand(clause.id);
                  handleDraftChange(clause.id, '');
                  setIsEditing(true);
                }}
                disabled={isClauseResponsesLoading}
              >
                <span>Respond</span>
                <i className='ri-chat-3-line fs-14 align-middle ms-1'></i>
              </Button>
            )}
          </div>

          {isExpanded && (hasAnySaved || isEditing) && (
            <div className='mt-3'>
              {responseList.map(response => {
                const notes = response?.respondNotes ?? '';
                if (!hasClauseResponse(notes)) return null;

                const evaluatorItem = isEvaluatorResponse(response);
                const canManage =
                  !isRespondClosed &&
                  lastOwnEditable &&
                  String(response.id) === String(lastOwnEditable.id);
                const isOwnEditing =
                  canManage &&
                  isEditing &&
                  String(ownResponse?.id ?? lastOwnEditable?.id ?? '') ===
                    String(response?.id ?? '');
                const metaDate = formatResponseMetaDate(response);
                const authorLabel = evaluatorItem
                  ? response.createdByLabel || 'PT Pertamina Patra Niaga'
                  : response.createdByLabel || 'Counter';

                if (isOwnEditing) return null;

                return (
                  <RespondMessageBubble
                    key={response.id ?? `${clause.id}-${metaDate}-${notes}`}
                    isEvaluator={evaluatorItem}
                    content={notes}
                    authorLabel={authorLabel}
                    metaDate={metaDate}
                    footer={
                      <>
                        {canAgreeLastCounter(responseList, response) &&
                          (!isRespondClosed || response.isLocked) && (
                            <div className='mt-3 pt-2 border-top'>
                              <AgreeLockButton
                                embedded
                                isLocked={response.isLocked}
                                onLock={
                                  !isRespondClosed &&
                                  isEvaluatorView &&
                                  response.id &&
                                  onLockResponse
                                    ? locked =>
                                        onLockResponse(response.id, locked)
                                    : undefined
                                }
                                disabled={isClauseResponsesSaving}
                              />
                            </div>
                          )}
                        {canManage &&
                          !response.isLocked &&
                          !isRespondClosed && (
                            <div className='mt-3 pt-2 border-top d-flex align-items-center gap-2'>
                              <Button
                                color='light'
                                size='sm'
                                className='border bg-white text-primary fw-medium fs-13 flex-grow-1'
                                onClick={() => {
                                  handleDraftChange(
                                    clause.id,
                                    lastOwnEditable?.respondNotes ?? ''
                                  );
                                  setIsEditing(true);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                color='light'
                                size='sm'
                                className='border bg-white text-danger fw-medium fs-13 flex-grow-1'
                                onClick={() => {
                                  Swal.fire({
                                    title: 'Are you sure?',
                                    text: 'This response note will be permanently deleted.',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonColor: '#f1556c',
                                    cancelButtonColor: '#6c757d',
                                    confirmButtonText: 'Yes, Delete!',
                                    cancelButtonText: 'Cancel',
                                  }).then(result => {
                                    if (result.isConfirmed) {
                                      handleClauseSecondaryAction(
                                        clause.id,
                                        true
                                      );
                                      setIsEditing(false);
                                    }
                                  });
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          )}
                      </>
                    }
                  />
                );
              })}

              {showRespondEditor && (
                <div className='mt-2'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <Label className='form-label fw-semibold text-dark fs-13 mb-0'>
                      Respond Notes
                    </Label>
                    {isDraftDirty && (
                      <span className='text-warning small fs-12 fw-medium d-flex align-items-center gap-1'>
                        <i className='ri-error-warning-line fs-14'></i>
                        Unsaved Changes
                      </span>
                    )}
                  </div>

                  <RespondNotesEditor
                    id={clause.id}
                    value={draftValue}
                    onChange={e => {
                      handleDraftChange(clause.id, e.target.value);
                      if (typeof setErrors === 'function') {
                        setErrors(prev => ({ ...prev, [clause.id]: '' }));
                      }
                    }}
                    isDraftDirty={isDraftDirty}
                  />

                  <div className='d-flex gap-2 mt-3 justify-content-end'>
                    <Button
                      color='light'
                      className='border bg-white text-dark fw-semibold px-4 rounded-3'
                      onClick={() => {
                        handleDraftChange(
                          clause.id,
                          hasOwnSaved
                            ? (lastOwnEditable?.respondNotes ?? '')
                            : ''
                        );
                        setIsEditing(false);
                        if (!hasOwnSaved && !hasAnySaved) {
                          toggleClauseExpand(clause.id);
                        }
                      }}
                      disabled={isClauseResponsesSaving}
                    >
                      Cancel
                    </Button>

                    <Button
                      color='primary'
                      className='fw-semibold px-4 rounded-3'
                      onClick={async () => {
                        await handleClauseSave(clause.id);
                        setIsEditing(false);
                      }}
                      disabled={isClauseResponsesSaving || !isDraftDirty}
                    >
                      {isClauseResponsesSaving ? 'Saving...' : 'Send Response'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Col>
  );
};

export default ClauseCard;
